import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useAuth } from '../context/AuthContext'
import { useSensorData } from '../context/SensorDataContext'
import {
  iotControllerApi,
  sensorDataApi,
  organizationApi
} from '../api/services'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts'
import toast from 'react-hot-toast'

// ── Constants ─────────────────────────────────────────────────────────────────
const CHART_COLORS = [
  '#3b82f6',
  '#22c55e',
  '#f97316',
  '#8b5cf6',
  '#06b6d4',
  '#ec4899',
  '#f59e0b',
  '#ef4444',
  '#14b8a6',
  '#a855f7'
]
const ONLINE_SET = new Set(['online', 'live', 'interval', 'warning'])

const DEFAULT_SHIFTS = [
  { id: 1, name: 'Shift 1', start: '06:00', end: '14:00' },
  { id: 2, name: 'Shift 2', start: '14:00', end: '22:00' },
  { id: 3, name: 'Shift 3', start: '22:00', end: '06:00' }
]

// ── WIDGET_REGISTRY ───────────────────────────────────────────────────────────
const WIDGET_REGISTRY = {
  fleet_overview: {
    label: 'Fleet Overview',
    description: 'Live controller status across the fleet',
    defaultSpan: 2,
    Icon: () => (
      <svg
        className='w-5 h-5'
        fill='none'
        viewBox='0 0 24 24'
        stroke='currentColor'
        strokeWidth={1.5}
      >
        <path
          strokeLinecap='round'
          strokeLinejoin='round'
          d='M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2'
        />
      </svg>
    )
  },
  kpi_summary: {
    label: 'KPI Summary',
    description: 'Min / Max / Avg per parameter',
    defaultSpan: 1,
    Icon: () => (
      <svg
        className='w-5 h-5'
        fill='none'
        viewBox='0 0 24 24'
        stroke='currentColor'
        strokeWidth={1.5}
      >
        <path
          strokeLinecap='round'
          strokeLinejoin='round'
          d='M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z'
        />
      </svg>
    )
  },
  line_chart: {
    label: 'Trend Chart',
    description: 'Parameter trends over time',
    defaultSpan: 2,
    Icon: () => (
      <svg
        className='w-5 h-5'
        fill='none'
        viewBox='0 0 24 24'
        stroke='currentColor'
        strokeWidth={1.5}
      >
        <path
          strokeLinecap='round'
          strokeLinejoin='round'
          d='M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941'
        />
      </svg>
    )
  },
  bar_chart: {
    label: 'Bar Chart',
    description: 'Period-over-period comparison',
    defaultSpan: 1,
    Icon: () => (
      <svg
        className='w-5 h-5'
        fill='none'
        viewBox='0 0 24 24'
        stroke='currentColor'
        strokeWidth={1.5}
      >
        <path
          strokeLinecap='round'
          strokeLinejoin='round'
          d='M7.5 14.25v2.25m3-4.5v4.5m3-6.75v6.75m3-9v9M6 20.25h12A2.25 2.25 0 0020.25 18V6A2.25 2.25 0 0018 3.75H6A2.25 2.25 0 003.75 6v12A2.25 2.25 0 006 20.25z'
        />
      </svg>
    )
  },
  area_chart: {
    label: 'Area Chart',
    description: 'Filled area trend visualization',
    defaultSpan: 2,
    Icon: () => (
      <svg
        className='w-5 h-5'
        fill='none'
        viewBox='0 0 24 24'
        stroke='currentColor'
        strokeWidth={1.5}
      >
        <path
          strokeLinecap='round'
          strokeLinejoin='round'
          d='M3 17l3-6 4 3 4-8 4 4v7H3z'
        />
      </svg>
    )
  },
  comparison: {
    label: 'Comparison',
    description: 'Multi-controller parameter comparison',
    defaultSpan: 2,
    Icon: () => (
      <svg
        className='w-5 h-5'
        fill='none'
        viewBox='0 0 24 24'
        stroke='currentColor'
        strokeWidth={1.5}
      >
        <path
          strokeLinecap='round'
          strokeLinejoin='round'
          d='M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5'
        />
      </svg>
    )
  },
  data_table: {
    label: 'Data Table',
    description: 'Aggregated data in tabular form',
    defaultSpan: 2,
    Icon: () => (
      <svg
        className='w-5 h-5'
        fill='none'
        viewBox='0 0 24 24'
        stroke='currentColor'
        strokeWidth={1.5}
      >
        <path
          strokeLinecap='round'
          strokeLinejoin='round'
          d='M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 01-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0112 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25'
        />
      </svg>
    )
  }
}

// ── Utilities ─────────────────────────────────────────────────────────────────
const toInputDT = d => {
  const p = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(
    d.getHours()
  )}:${p(d.getMinutes())}`
}
const defaultFrom = () => {
  const d = new Date()
  d.setDate(d.getDate() - 7)
  d.setHours(0, 0, 0, 0)
  return toInputDT(d)
}
const defaultTo = () => toInputDT(new Date())

const parseUTC = str => {
  if (!str) return new Date()
  return /[Z+\-]\d*$/.test(str) ? new Date(str) : new Date(str + 'Z')
}

const fmtMins = m =>
  `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(
    2,
    '0'
  )}`
const toMins = t => {
  const [h, m] = t.split(':').map(Number)
  return h * 60 + m
}

const buildShiftsFromOrg = (start, end) => {
  if (!start || !end) return DEFAULT_SHIFTS
  const s = toMins(start),
    e = toMins(end)
  const dur = (e - s + 1440) % 1440
  if (!dur) return DEFAULT_SHIFTS
  const count = Math.round(1440 / dur)
  if (Math.abs(count * dur - 1440) <= 5) {
    return Array.from({ length: count }, (_, i) => ({
      id: i + 1,
      name: `Shift ${i + 1}`,
      start: fmtMins((s + i * dur) % 1440),
      end: fmtMins((s + (i + 1) * dur) % 1440)
    }))
  }
  return [{ id: 1, name: 'Shift 1', start, end }]
}

const buildIntervalRows = readings => {
  const p = n => String(n).padStart(2, '0')
  return readings.map(r => {
    const ts = parseUTC(r.dynamic_payload?.Timestamp || r.recorded_at)
    const label = `${ts.getMonth() + 1}/${ts.getDate()} ${p(ts.getHours())}:${p(
      ts.getMinutes()
    )}`
    const row = { _label: label }
    Object.entries(r.dynamic_payload?.Data || {}).forEach(([k, raw]) => {
      const v = typeof raw === 'number' ? raw : parseFloat(raw)
      if (!isNaN(v)) row[k] = +v.toFixed(3)
    })
    return row
  })
}

const buildMerged = aggData => {
  const allLabels = new Set()
  Object.values(aggData).forEach(rows =>
    rows.forEach(r => allLabels.add(r._label))
  )
  return [...allLabels].sort().map(label => {
    const row = { _label: label }
    Object.entries(aggData).forEach(([cid, rows]) => {
      const match = rows.find(r => r._label === label)
      if (match)
        Object.entries(match).forEach(([k, v]) => {
          if (k !== '_label') row[`${cid}__${k}`] = v
        })
    })
    return row
  })
}

// ── DateTimePicker12h ─────────────────────────────────────────────────────────
const DateTimePicker12h = ({ value, onChange, className }) => {
  const [date, setDate] = useState('')
  const [hour, setHour] = useState('12')
  const [min, setMin] = useState('00')
  const [ampm, setAmpm] = useState('AM')

  useEffect(() => {
    if (!value) return
    const [d, t] = value.split('T')
    const [h, m] = (t || '00:00').split(':').map(Number)
    setDate(d || '')
    setMin(String(m).padStart(2, '0'))
    if (h === 0) {
      setHour('12')
      setAmpm('AM')
    } else if (h < 12) {
      setHour(String(h))
      setAmpm('AM')
    } else if (h === 12) {
      setHour('12')
      setAmpm('PM')
    } else {
      setHour(String(h - 12))
      setAmpm('PM')
    }
  }, [value])

  const emit = (d, h, m, ap) => {
    let h24 = parseInt(h, 10)
    if (ap === 'AM' && h24 === 12) h24 = 0
    if (ap === 'PM' && h24 !== 12) h24 += 12
    onChange(`${d}T${String(h24).padStart(2, '0')}:${m}`)
  }
  const fc =
    'text-sm text-gray-700 bg-white outline-none px-1.5 py-2 border-r border-gray-100 last:border-r-0'
  return (
    <div className={`flex items-center ${className || ''}`}>
      <input
        type='date'
        value={date}
        className={`${fc} min-w-[120px]`}
        onChange={e => {
          setDate(e.target.value)
          emit(e.target.value, hour, min, ampm)
        }}
      />
      <select
        value={hour}
        className={fc}
        style={{ width: 48 }}
        onChange={e => {
          setHour(e.target.value)
          emit(date, e.target.value, min, ampm)
        }}
      >
        {Array.from({ length: 12 }, (_, i) => String(i + 1)).map(h => (
          <option key={h} value={h}>
            {h.padStart(2, '0')}
          </option>
        ))}
      </select>
      <span className='text-gray-300 text-sm select-none'>:</span>
      <select
        value={min}
        className={fc}
        style={{ width: 48 }}
        onChange={e => {
          setMin(e.target.value)
          emit(date, hour, e.target.value, ampm)
        }}
      >
        {Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0')).map(
          m => (
            <option key={m} value={m}>
              {m}
            </option>
          )
        )}
      </select>
      <select
        value={ampm}
        className={`${fc} pr-2`}
        style={{ width: 52 }}
        onChange={e => {
          setAmpm(e.target.value)
          emit(date, hour, min, e.target.value)
        }}
      >
        <option value='AM'>AM</option>
        <option value='PM'>PM</option>
      </select>
    </div>
  )
}

// ── ChartTooltip ──────────────────────────────────────────────────────────────
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null
  return (
    <div className='bg-gray-900 text-white rounded-lg px-3 py-2 shadow-2xl min-w-[160px]'>
      <p className='text-[10px] text-gray-300 mb-1'>{label}</p>
      {payload.map(p => (
        <div
          key={p.dataKey}
          className='flex items-center justify-between gap-3'
        >
          <div className='flex items-center gap-1.5'>
            <span
              className='w-1.5 h-1.5 rounded-full'
              style={{ backgroundColor: p.color }}
            />
            <span className='text-[10px] text-gray-300'>{p.name}</span>
          </div>
          <span className='text-[10px] font-mono font-bold'>
            {p.value != null ? Number(p.value).toFixed(2) : '—'}
          </span>
        </div>
      ))}
    </div>
  )
}

// ── ControllerMultiSelect ─────────────────────────────────────────────────────
const ControllerMultiSelect = ({ controllers, selected, onChange }) => {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    const h = e => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])
  const toggle = id => {
    if (selected.includes(id)) {
      if (selected.length > 1) onChange(selected.filter(s => s !== id))
    } else onChange([...selected, id])
  }
  const label =
    selected.length === 0
      ? 'Select controller…'
      : selected.length === 1
      ? controllers.find(c => c.id === selected[0])?.name || selected[0]
      : `${selected.length} controllers`
  return (
    <div ref={ref} className='relative'>
      <button
        onClick={() => setOpen(o => !o)}
        className='flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 hover:border-blue-400 min-w-[200px] justify-between transition-colors'
      >
        <span className='truncate'>{label}</span>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${
            open ? 'rotate-180' : ''
          }`}
          fill='none'
          viewBox='0 0 24 24'
          stroke='currentColor'
          strokeWidth={2}
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            d='M19 9l-7 7-7-7'
          />
        </svg>
      </button>
      {open && (
        <div className='absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 min-w-[240px] max-h-64 overflow-y-auto'>
          {controllers.length === 0 ? (
            <div className='px-4 py-3 text-sm text-gray-400'>
              No controllers found
            </div>
          ) : (
            controllers.map(c => (
              <label
                key={c.id}
                className='flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 cursor-pointer'
              >
                <input
                  type='checkbox'
                  checked={selected.includes(c.id)}
                  onChange={() => toggle(c.id)}
                  className='w-4 h-4 rounded border-gray-300 accent-blue-600'
                />
                <div className='min-w-0'>
                  <div className='text-sm font-medium text-gray-700 truncate'>
                    {c.name}
                  </div>
                  {c.topic && (
                    <div className='text-xs text-gray-400 truncate'>
                      {c.topic}
                    </div>
                  )}
                </div>
              </label>
            ))
          )}
        </div>
      )}
    </div>
  )
}

// ── EmptyState ────────────────────────────────────────────────────────────────
const EmptyState = ({ message, icon }) => (
  <div className='flex flex-col items-center justify-center h-48 text-gray-400 select-none'>
    {icon || (
      <svg
        className='w-10 h-10 mb-3 opacity-25'
        fill='none'
        viewBox='0 0 24 24'
        stroke='currentColor'
      >
        <path
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth={1.5}
          d='M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z'
        />
      </svg>
    )}
    <p className='text-sm text-center px-4'>{message}</p>
  </div>
)

// ── WidgetCard (drag-and-drop wrapper) ────────────────────────────────────────
const WidgetCard = ({
  widget,
  isEditMode,
  isDragOver,
  onRemove,
  onSpanChange,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  children
}) => (
  <div
    id={`widget-${widget.id}`}
    draggable={isEditMode}
    onDragStart={e => isEditMode && onDragStart(e, widget.id)}
    onDragOver={e => {
      e.preventDefault()
      onDragOver(e, widget.id)
    }}
    onDrop={e => onDrop(e, widget.id)}
    onDragEnd={onDragEnd}
    className={[
      'bg-white rounded-xl border shadow-sm overflow-hidden flex flex-col relative transition-all duration-150',
      widget.span === 2 ? 'col-span-2' : '',
      isEditMode ? 'cursor-grab active:cursor-grabbing' : '',
      isDragOver ? 'border-blue-400 shadow-md scale-[1.01]' : 'border-gray-200'
    ]
      .filter(Boolean)
      .join(' ')}
  >
    {/* Title bar */}
    <div className='flex items-center gap-2 px-4 py-3 border-b border-gray-100 bg-white'>
      {isEditMode && (
        <div className='flex-shrink-0 text-gray-300' title='Drag to reorder'>
          <svg className='w-4 h-4' fill='currentColor' viewBox='0 0 16 16'>
            <circle cx='5' cy='4' r='1.2' />
            <circle cx='11' cy='4' r='1.2' />
            <circle cx='5' cy='8' r='1.2' />
            <circle cx='11' cy='8' r='1.2' />
            <circle cx='5' cy='12' r='1.2' />
            <circle cx='11' cy='12' r='1.2' />
          </svg>
        </div>
      )}
      <h3 className='text-sm font-bold text-gray-700 flex-1 truncate'>
        {widget.title}
      </h3>
      {isEditMode && (
        <div className='flex items-center gap-1.5 flex-shrink-0'>
          {/* Column span toggle */}
          <div
            className='flex rounded-md border border-gray-200 overflow-hidden'
            title='Column width'
          >
            {[1, 2].map(col => (
              <button
                key={col}
                onClick={() => onSpanChange(col)}
                className={`flex items-center gap-1 px-2 py-1 text-[11px] font-semibold transition-colors border-r border-gray-200 last:border-r-0 ${
                  widget.span === col
                    ? 'bg-blue-900 text-white'
                    : 'bg-white text-gray-400 hover:bg-blue-50 hover:text-blue-700'
                }`}
              >
                {col === 1 ? (
                  <svg
                    className='w-3 h-3'
                    viewBox='0 0 12 12'
                    fill='currentColor'
                  >
                    <rect x='0' y='0' width='5' height='12' rx='1' />
                    <rect
                      x='7'
                      y='0'
                      width='5'
                      height='12'
                      rx='1'
                      opacity='0.25'
                    />
                  </svg>
                ) : (
                  <svg
                    className='w-3 h-3'
                    viewBox='0 0 12 12'
                    fill='currentColor'
                  >
                    <rect x='0' y='0' width='12' height='12' rx='1' />
                  </svg>
                )}
                {col}
              </button>
            ))}
          </div>
          {/* Remove */}
          <button
            onClick={onRemove}
            className='p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors'
            title='Remove widget'
          >
            <svg
              className='w-4 h-4'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
              strokeWidth={2}
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                d='M6 18L18 6M6 6l12 12'
              />
            </svg>
          </button>
        </div>
      )}
    </div>
    {/* Content */}
    <div className='flex-1 p-4 min-h-0 relative'>{children}</div>
  </div>
)

// ── FleetOverviewWidget ───────────────────────────────────────────────────────
const FleetOverviewWidget = ({ controllers, livePayloads }) => {
  const resolve = ctrl =>
    ONLINE_SET.has(
      (livePayloads[ctrl.id]?.status || ctrl.status || 'offline').toLowerCase()
    )
      ? 'online'
      : 'offline'
  const online = controllers.filter(c => resolve(c) === 'online').length
  const offline = controllers.length - online
  if (!controllers.length) return <EmptyState message='No controllers loaded' />
  return (
    <div className='flex flex-col gap-3 h-full'>
      <div className='grid grid-cols-3 gap-3'>
        {[
          {
            label: 'Total',
            value: controllers.length,
            cls: 'text-gray-800',
            bg: 'bg-gray-50'
          },
          {
            label: 'Online',
            value: online,
            cls: 'text-green-600',
            bg: 'bg-green-50'
          },
          {
            label: 'Offline',
            value: offline,
            cls: 'text-red-500',
            bg: 'bg-red-50'
          }
        ].map(s => (
          <div
            key={s.label}
            className={`${s.bg} rounded-lg px-3 py-2.5 text-center border border-transparent`}
          >
            <div className={`text-2xl font-black ${s.cls}`}>{s.value}</div>
            <div className='text-[10px] font-semibold text-gray-400 uppercase tracking-wide'>
              {s.label}
            </div>
          </div>
        ))}
      </div>
      <div className='flex-1 overflow-y-auto divide-y divide-gray-50'>
        {controllers.map(c => {
          const status = resolve(c)
          return (
            <div key={c.id} className='flex items-center gap-3 py-2'>
              <span
                className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  status === 'online' ? 'bg-green-500' : 'bg-gray-300'
                }`}
              />
              <span className='text-sm text-gray-700 truncate flex-1'>
                {c.name}
              </span>
              <span
                className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                  status === 'online'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-400'
                }`}
              >
                {status}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── KPISummaryWidget ──────────────────────────────────────────────────────────
const KPISummaryWidget = ({ rawReadings, params, fetched }) => {
  const stats = useMemo(() => {
    const acc = {}
    Object.values(rawReadings).forEach(readings =>
      readings.forEach(r =>
        Object.entries(r.dynamic_payload?.Data || {}).forEach(([k, raw]) => {
          const v = typeof raw === 'number' ? raw : parseFloat(raw)
          if (isNaN(v)) return
          if (!acc[k]) acc[k] = { sum: 0, n: 0, min: Infinity, max: -Infinity }
          const s = acc[k]
          s.sum += v
          s.n++
          if (v < s.min) s.min = v
          if (v > s.max) s.max = v
        })
      )
    )
    return acc
  }, [rawReadings])

  if (!fetched) return <EmptyState message='Click Generate to load data' />
  const keys = Object.keys(stats)
  if (!keys.length) return <EmptyState message='No data for selected period' />

  return (
    <div className='grid grid-cols-2 gap-2.5 overflow-y-auto max-h-80'>
      {keys.map((k, i) => {
        const s = stats[k]
        const label = params[k]?.display_name || k
        const unit = params[k]?.unit ? ` ${params[k].unit}` : ''
        return (
          <div
            key={k}
            className='bg-gray-50 rounded-lg p-3 border border-gray-100'
          >
            <div className='flex items-center justify-between mb-1'>
              <span
                className='text-xs font-semibold text-gray-500 truncate'
                title={label}
              >
                {label}
              </span>
              <div
                className='w-2 h-2 rounded-full flex-shrink-0'
                style={{
                  backgroundColor: CHART_COLORS[i % CHART_COLORS.length]
                }}
              />
            </div>
            <div className='text-xl font-black text-gray-800'>
              {(s.sum / s.n).toFixed(2)}
              <span className='text-xs font-normal text-gray-400 ml-1'>
                {unit}
              </span>
            </div>
            <div className='text-[9px] text-gray-400 mb-1.5'>avg</div>
            <div className='flex gap-3'>
              <div>
                <div className='text-xs font-semibold text-green-600'>
                  {s.min.toFixed(2)}
                </div>
                <div className='text-[9px] text-gray-400'>min</div>
              </div>
              <div>
                <div className='text-xs font-semibold text-red-500'>
                  {s.max.toFixed(2)}
                </div>
                <div className='text-[9px] text-gray-400'>max</div>
              </div>
              <div>
                <div className='text-xs font-semibold text-gray-500'>
                  {s.n.toLocaleString()}
                </div>
                <div className='text-[9px] text-gray-400'>pts</div>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── LineChartWidget ───────────────────────────────────────────────────────────
const LineChartWidget = ({ mergedData, series, fetched }) => {
  if (!fetched) return <EmptyState message='Click Generate to load data' />
  if (!mergedData.length)
    return <EmptyState message='No data for selected period' />
  return (
    <ResponsiveContainer width='100%' height={280}>
      <LineChart
        data={mergedData}
        margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray='3 3' stroke='#f1f5f9' />
        <XAxis
          dataKey='_label'
          tick={{ fontSize: 11, fill: '#94a3b8' }}
          tickLine={false}
        />
        <YAxis
          tick={{ fontSize: 11, fill: '#94a3b8' }}
          tickLine={false}
          axisLine={false}
          width={50}
        />
        <Tooltip content={<ChartTooltip />} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        {series.map(s => (
          <Line
            key={s.key}
            type='monotone'
            dataKey={s.key}
            name={s.name}
            stroke={s.color}
            strokeWidth={2}
            dot={false}
            strokeDasharray={s.dash}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  )
}

// ── BarChartWidget ────────────────────────────────────────────────────────────
const BarChartWidget = ({ mergedData, series, fetched }) => {
  if (!fetched) return <EmptyState message='Click Generate to load data' />
  if (!mergedData.length)
    return <EmptyState message='No data for selected period' />
  return (
    <ResponsiveContainer width='100%' height={280}>
      <BarChart
        data={mergedData.slice(-30)}
        margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
      >
        <CartesianGrid
          strokeDasharray='3 3'
          stroke='#f1f5f9'
          vertical={false}
        />
        <XAxis
          dataKey='_label'
          tick={{ fontSize: 11, fill: '#94a3b8' }}
          tickLine={false}
        />
        <YAxis
          tick={{ fontSize: 11, fill: '#94a3b8' }}
          tickLine={false}
          axisLine={false}
          width={50}
        />
        <Tooltip content={<ChartTooltip />} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        {series.slice(0, 6).map(s => (
          <Bar
            key={s.key}
            dataKey={s.key}
            name={s.name}
            fill={s.color}
            radius={[3, 3, 0, 0]}
            maxBarSize={30}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  )
}

// ── AreaChartWidget ───────────────────────────────────────────────────────────
const AreaChartWidget = ({ mergedData, series, fetched }) => {
  if (!fetched) return <EmptyState message='Click Generate to load data' />
  if (!mergedData.length)
    return <EmptyState message='No data for selected period' />
  return (
    <ResponsiveContainer width='100%' height={280}>
      <AreaChart
        data={mergedData}
        margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
      >
        <defs>
          {series.map((s, i) => (
            <linearGradient
              key={s.key}
              id={`adg-${i}`}
              x1='0'
              y1='0'
              x2='0'
              y2='1'
            >
              <stop offset='5%' stopColor={s.color} stopOpacity={0.2} />
              <stop offset='95%' stopColor={s.color} stopOpacity={0} />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid strokeDasharray='3 3' stroke='#f1f5f9' />
        <XAxis
          dataKey='_label'
          tick={{ fontSize: 11, fill: '#94a3b8' }}
          tickLine={false}
        />
        <YAxis
          tick={{ fontSize: 11, fill: '#94a3b8' }}
          tickLine={false}
          axisLine={false}
          width={50}
        />
        <Tooltip content={<ChartTooltip />} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        {series.map((s, i) => (
          <Area
            key={s.key}
            type='monotone'
            dataKey={s.key}
            name={s.name}
            stroke={s.color}
            strokeWidth={2}
            fill={`url(#adg-${i})`}
            dot={false}
            strokeDasharray={s.dash}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  )
}

// ── ComparisonWidget ──────────────────────────────────────────────────────────
const ComparisonWidget = ({
  mergedData,
  discoveredParams,
  selectedControllers,
  controllerMap,
  params,
  fetched
}) => {
  const [selectedParam, setSelectedParam] = useState('')
  const [dropOpen, setDropOpen] = useState(false)
  const dropRef = useRef(null)

  useEffect(() => {
    if (discoveredParams.length && !selectedParam)
      setSelectedParam(discoveredParams[0])
  }, [discoveredParams, selectedParam])

  // Close dropdown on outside click
  useEffect(() => {
    const h = e => {
      if (dropRef.current && !dropRef.current.contains(e.target))
        setDropOpen(false)
    }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  const paramLabel = p => params[p]?.display_name || p

  const ctrlColors = selectedControllers.reduce((acc, cid, i) => {
    acc[cid] = CHART_COLORS[i % CHART_COLORS.length]
    return acc
  }, {})

  if (!fetched) return <EmptyState message='Click Generate to load data' />
  if (!mergedData.length)
    return <EmptyState message='No data for selected period' />
  if (!discoveredParams.length)
    return <EmptyState message='No parameters found in data' />

  return (
    <div className='flex flex-col gap-3 h-full'>
      {/* Toolbar */}
      <div className='flex items-center gap-3 flex-wrap'>
        {/* Parameter single-select dropdown */}
        <div ref={dropRef} className='relative'>
          <button
            onClick={() => setDropOpen(o => !o)}
            className='flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:border-blue-400 transition-colors min-w-[160px] justify-between'
          >
            <span className='truncate'>
              {selectedParam ? paramLabel(selectedParam) : 'Select parameter…'}
            </span>
            <svg
              className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${
                dropOpen ? 'rotate-180' : ''
              }`}
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
              strokeWidth={2}
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                d='M19 9l-7 7-7-7'
              />
            </svg>
          </button>
          {dropOpen && (
            <div className='absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 min-w-[200px] max-h-64 overflow-y-auto'>
              {discoveredParams.map(p => (
                <button
                  key={p}
                  onClick={() => {
                    setSelectedParam(p)
                    setDropOpen(false)
                  }}
                  className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                    selectedParam === p
                      ? 'bg-blue-900 text-white font-semibold'
                      : 'text-gray-700 hover:bg-blue-50'
                  }`}
                >
                  {paramLabel(p)}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Controller legend */}
        {selectedControllers.map((cid, i) => (
          <span
            key={cid}
            className='inline-flex items-center gap-1.5 text-[11px] font-semibold'
            style={{ color: ctrlColors[cid] }}
          >
            <svg width='18' height='4' viewBox='0 0 18 4'>
              <line
                x1='0'
                y1='2'
                x2='18'
                y2='2'
                stroke={ctrlColors[cid]}
                strokeWidth='2'
                strokeDasharray={i > 0 ? '5 3' : undefined}
              />
            </svg>
            {controllerMap[cid] || cid}
          </span>
        ))}
      </div>

      {/* Chart for selected parameter */}
      {selectedParam && (
        <ResponsiveContainer width='100%' height={260}>
          <LineChart
            data={mergedData}
            margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray='3 3' stroke='#f1f5f9' />
            <XAxis
              dataKey='_label'
              tick={{ fontSize: 11, fill: '#94a3b8' }}
              tickLine={false}
              interval='preserveStartEnd'
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#94a3b8' }}
              tickLine={false}
              axisLine={false}
              width={50}
            />
            <Tooltip content={<ChartTooltip />} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {selectedControllers.map((cid, i) => (
              <Line
                key={`${cid}__${selectedParam}`}
                type='monotone'
                dataKey={`${cid}__${selectedParam}`}
                name={controllerMap[cid] || cid}
                stroke={ctrlColors[cid]}
                strokeWidth={2.5}
                dot={false}
                strokeDasharray={i > 0 ? '5 3' : undefined}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  )
}

// ── DataTableWidget ───────────────────────────────────────────────────────────
const DataTableWidget = ({
  mergedData,
  discoveredParams,
  selectedControllers,
  controllerMap,
  params,
  fetched
}) => {
  if (!fetched) return <EmptyState message='Click Generate to load data' />
  if (!mergedData.length)
    return <EmptyState message='No data for selected period' />
  const multi = selectedControllers.length > 1
  const cols = selectedControllers.flatMap(cid =>
    discoveredParams.map(p => ({
      key: `${cid}__${p}`,
      label: multi
        ? `${controllerMap[cid] || cid} / ${params[p]?.display_name || p}`
        : params[p]?.display_name || p
    }))
  )
  return (
    <div className='overflow-auto max-h-72'>
      <table className='w-full text-xs'>
        <thead>
          <tr className='bg-gray-50 border-b border-gray-200 sticky top-0'>
            <th className='text-left px-3 py-2 font-semibold text-gray-500 whitespace-nowrap'>
              Period
            </th>
            {cols.map(c => (
              <th
                key={c.key}
                className='text-right px-3 py-2 font-semibold text-gray-500 whitespace-nowrap'
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {mergedData.map((row, idx) => (
            <tr
              key={idx}
              className='border-b border-gray-50 hover:bg-blue-50/30'
            >
              <td className='px-3 py-2 font-medium text-gray-600 whitespace-nowrap'>
                {row._label}
              </td>
              {cols.map(c => (
                <td
                  key={c.key}
                  className='px-3 py-2 text-right font-mono text-gray-700'
                >
                  {row[c.key] != null ? row[c.key].toFixed(2) : '—'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── WidgetPickerModal ─────────────────────────────────────────────────────────
const WidgetPickerModal = ({ onAdd, onClose }) => (
  <div className='fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 '>
    <div className='bg-white rounded-2xl shadow-2xl w-full max-w-lg'>
      <div className='flex items-center justify-between px-6 py-4 border-b overflow-y-auto max-h-48 border-gray-100'>
        <h2 className='text-base font-bold text-gray-800'>Add Widget</h2>
        <button
          onClick={onClose}
          className='p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 transition-colors'
        >
          <svg
            className='w-4 h-4'
            fill='none'
            viewBox='0 0 24 24'
            stroke='currentColor'
            strokeWidth={2}
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              d='M6 18L18 6M6 6l12 12'
            />
          </svg>
        </button>
      </div>
      <div className='p-6'>
        <div className='grid grid-cols-3 gap-3'>
          {Object.entries(WIDGET_REGISTRY).map(([type, meta]) => (
            <button
              key={type}
              onClick={() => onAdd(type)}
              className='flex flex-col items-center gap-2.5 p-4 rounded-xl border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-all duration-150 text-center group'
            >
              <div className='w-11 h-11 bg-gray-100 group-hover:bg-blue-100 rounded-xl flex items-center justify-center text-gray-500 group-hover:text-blue-600 transition-colors'>
                <meta.Icon />
              </div>
              <div>
                <div className='text-sm font-semibold text-gray-700'>
                  {meta.label}
                </div>
                <div className='text-[10px] text-gray-400 mt-0.5 leading-tight'>
                  {meta.description}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
      <div className='px-6 pb-5'>
        <button
          onClick={onClose}
          className='w-full py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors'
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
)

// ── Main Component ────────────────────────────────────────────────────────────
const AnalyticsDashboard = () => {
  const { user, activeTenant } = useAuth()
  const { livePayloads, paramMappingsCache, loadParamMappings } =
    useSensorData()
  const resolvedTenantId =
    (typeof activeTenant === 'object' ? activeTenant?.id : activeTenant) ??
    user?.tenant_id

  // Controllers
  const [controllers, setControllers] = useState([])
  const [selectedControllers, setSelectedControllers] = useState([])

  // Filter bar
  const [fromDT, setFromDT] = useState(defaultFrom)
  const [toDT, setToDT] = useState(defaultTo)

  // Shared data (single fetch for all widgets)
  const [rawReadings, setRawReadings] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [fetched, setFetched] = useState(false)

  // Org shifts
  const [shifts, setShifts] = useState(DEFAULT_SHIFTS)

  // Layout (persisted to API + localStorage cache)
  const [activeWidgets, setActiveWidgets] = useState([])
  const [layoutLoading, setLayoutLoading] = useState(true)
  const layoutLoadedRef = useRef(false)
  const saveTimerRef = useRef(null)
  const [isEditMode, setIsEditMode] = useState(false)
  const [showWidgetPicker, setShowWidgetPicker] = useState(false)
  const [lastAddedId, setLastAddedId] = useState(null)

  // Drag-and-drop
  const dragSrcRef = useRef(null)
  const [dragOverId, setDragOverId] = useState(null)

  // ── Load controllers + params + org shifts once tenant is known ───────────
  useEffect(() => {
    if (!resolvedTenantId) return
    iotControllerApi
      .getAll(resolvedTenantId)
      .then(res => {
        const list = res.data?.data || []
        setControllers(list)
        if (list.length) {
          setSelectedControllers([list[0].id])
          loadParamMappings(list)
        }
      })
      .catch(() => toast.error('Failed to load controllers'))
    organizationApi
      .getAll({ tenantId: resolvedTenantId })
      .then(res => {
        const orgs = res.data?.data || []
        const first = Array.isArray(orgs) ? orgs[0] : null
        if (first?.shift_start_time)
          setShifts(
            buildShiftsFromOrg(first.shift_start_time, first.shift_end_time)
          )
      })
      .catch(() => {})
  }, [resolvedTenantId, loadParamMappings])

  // ── localStorage persistence ─────────────────────────────────────────────
  const STORAGE_KEY = 'adv-dashboard-layout-v1'

  const saveToStorage = useCallback(widgets => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(widgets))
    } catch (e) {
      console.warn('Failed to save dashboard layout to localStorage:', e)
    }
  }, [])

  const loadFromStorage = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      return stored ? JSON.parse(stored) : []
    } catch (e) {
      console.warn('Failed to load dashboard layout from localStorage:', e)
      return []
    }
  }, [])

  // ── Debounced save ─────────────────────────────────────────────────────
  const saveToDB = useCallback(
    widgets => {
      clearTimeout(saveTimerRef.current)
      saveTimerRef.current = setTimeout(() => {
        saveToStorage(widgets)
      }, 800)
    },
    [saveToStorage]
  )

  // ── Load layout from localStorage on mount ──────────────────────────────
  useEffect(() => {
    const widgets = loadFromStorage()
    if (Array.isArray(widgets) && widgets.length) {
      setActiveWidgets(widgets)
    }
    // else: stay at [] — user sees empty state
    setLayoutLoading(false)
    layoutLoadedRef.current = true
  }, [loadFromStorage])

  // ── Generate: single shared data fetch ────────────────────────────────────
  const handleGenerate = useCallback(async () => {
    if (!selectedControllers.length)
      return toast.error('Select at least one controller')
    if (
      !fromDT?.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/) ||
      !toDT?.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/)
    )
      return toast.error('Set a valid date range')
    setIsLoading(true)
    setFetched(false)
    try {
      const results = await Promise.all(
        selectedControllers.map(cid =>
          sensorDataApi
            .getRange(cid, resolvedTenantId, fromDT, toDT, 2000)
            .then(r => ({ cid, readings: r.data?.data || [] }))
            .catch(() => {
              toast.error(
                `No data for ${
                  controllers.find(c => c.id === cid)?.name || cid
                }`
              )
              return { cid, readings: [] }
            })
        )
      )
      const map = {}
      results.forEach(({ cid, readings }) => {
        map[cid] = readings
      })
      setRawReadings(map)
      setFetched(true)
      const total = results.reduce((s, r) => s + r.readings.length, 0)
      if (!total) toast('No readings in this period', { icon: 'ℹ️' })
      else toast.success(`${total.toLocaleString()} readings loaded`)
    } finally {
      setIsLoading(false)
    }
  }, [selectedControllers, fromDT, toDT, resolvedTenantId, controllers])

  // ── Derived shared data ────────────────────────────────────────────────────
  const aggregatedData = useMemo(() => {
    const out = {}
    Object.entries(rawReadings).forEach(([cid, readings]) => {
      out[cid] = buildIntervalRows(readings)
    })
    return out
  }, [rawReadings])

  const mergedChartData = useMemo(
    () => buildMerged(aggregatedData),
    [aggregatedData]
  )

  const discoveredParams = useMemo(() => {
    const set = new Set()
    Object.values(rawReadings).forEach(readings =>
      readings.forEach(r =>
        Object.keys(r.dynamic_payload?.Data || {}).forEach(k => set.add(k))
      )
    )
    return [...set].sort()
  }, [rawReadings])

  const chartSeries = useMemo(() => {
    const active = selectedControllers.filter(
      cid => aggregatedData[cid]?.length
    )
    const multi = active.length > 1
    const series = []
    active.forEach((cid, ci) => {
      const ctrlName = controllers.find(c => c.id === cid)?.name || cid
      discoveredParams.forEach((param, pi) => {
        series.push({
          key: `${cid}__${param}`,
          name: multi
            ? `${ctrlName} — ${param}`
            : paramMappingsCache[cid]?.[param]?.display_name || param,
          color:
            CHART_COLORS[
              (ci * discoveredParams.length + pi) % CHART_COLORS.length
            ],
          dash: multi && ci > 0 ? '5 3' : undefined
        })
      })
    })
    return series
  }, [
    aggregatedData,
    selectedControllers,
    discoveredParams,
    controllers,
    paramMappingsCache
  ])

  const controllerMap = useMemo(
    () => Object.fromEntries(controllers.map(c => [c.id, c.name])),
    [controllers]
  )

  // Primary controller's param mappings
  const params = paramMappingsCache[selectedControllers[0]] || {}

  // ── Auto-scroll to newly added widget ─────────────────────────────────────
  useEffect(() => {
    if (!lastAddedId) return
    const el = document.getElementById(`widget-${lastAddedId}`)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      setLastAddedId(null)
    }
  }, [activeWidgets, lastAddedId])

  // ── Widget add / remove / reset ────────────────────────────────────────────
  // Save synchronously inside each updater so navigation away after "Done"
  // never loses the layout (useEffect fires too late on unmount).
  const handleAddWidget = useCallback(
    type => {
      const meta = WIDGET_REGISTRY[type]
      const id = `w-${Date.now()}`
      setActiveWidgets(prev => {
        const next = [
          ...prev,
          { id, type, title: meta.label, span: meta.defaultSpan }
        ]
        saveToDB(next)
        return next
      })
      setLastAddedId(id)
      setShowWidgetPicker(false)
    },
    [saveToDB]
  )

  const handleRemoveWidget = useCallback(
    id => {
      setActiveWidgets(prev => {
        const next = prev.filter(w => w.id !== id)
        saveToDB(next)
        return next
      })
    },
    [saveToDB]
  )

  const handleSpanChange = useCallback(
    (id, span) => {
      setActiveWidgets(prev => {
        const next = prev.map(w => (w.id === id ? { ...w, span } : w))
        saveToDB(next)
        return next
      })
    },
    [saveToDB]
  )

  const handleResetLayout = useCallback(() => {
    setIsEditMode(false)
    setLayoutLoading(true)
    // Clear localStorage and restore default (empty layout)
    localStorage.removeItem(STORAGE_KEY)
    setActiveWidgets([])
    setLayoutLoading(false)
    toast.success('Layout reset to default')
  }, [])

  // ── Drag-and-drop handlers ─────────────────────────────────────────────────
  const handleDragStart = useCallback((e, id) => {
    dragSrcRef.current = id
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', id)
  }, [])

  const handleDragOver = useCallback((e, id) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDragOverId(id)
  }, [])

  const handleDrop = useCallback(
    (e, targetId) => {
      e.preventDefault()
      const srcId = dragSrcRef.current
      if (!srcId || srcId === targetId) {
        setDragOverId(null)
        return
      }
      setActiveWidgets(prev => {
        const from = prev.findIndex(w => w.id === srcId)
        const to = prev.findIndex(w => w.id === targetId)
        if (from === -1 || to === -1) return prev
        const next = [...prev]
        const [item] = next.splice(from, 1)
        next.splice(to, 0, item)
        saveToDB(next)
        return next
      })
      dragSrcRef.current = null
      setDragOverId(null)
    },
    [saveToDB]
  )

  const handleDragEnd = useCallback(() => {
    dragSrcRef.current = null
    setDragOverId(null)
  }, [])

  // ── Render widget content ──────────────────────────────────────────────────
  const sharedChart = {
    mergedData: mergedChartData,
    series: chartSeries,
    fetched
  }

  const renderContent = widget => {
    switch (widget.type) {
      case 'fleet_overview':
        return (
          <FleetOverviewWidget
            controllers={controllers}
            livePayloads={livePayloads}
          />
        )
      case 'kpi_summary':
        return (
          <KPISummaryWidget
            rawReadings={rawReadings}
            params={params}
            fetched={fetched}
          />
        )
      case 'line_chart':
        return <LineChartWidget {...sharedChart} />
      case 'bar_chart':
        return <BarChartWidget {...sharedChart} />
      case 'area_chart':
        return <AreaChartWidget {...sharedChart} />
      case 'comparison':
        return (
          <ComparisonWidget
            mergedData={mergedChartData}
            chartSeries={chartSeries}
            discoveredParams={discoveredParams}
            selectedControllers={selectedControllers}
            controllerMap={controllerMap}
            params={params}
            fetched={fetched}
          />
        )
      case 'data_table':
        return (
          <DataTableWidget
            mergedData={mergedChartData}
            discoveredParams={discoveredParams}
            selectedControllers={selectedControllers}
            controllerMap={controllerMap}
            params={params}
            fetched={fetched}
          />
        )
      default:
        return null
    }
  }

  // ── No-controller empty state ──────────────────────────────────────────────
  const noControllerSelected = selectedControllers.length === 0

  return (
    <div className='flex flex-col min-h-full bg-gray-50'>
      {/* ── Page header ──────────────────────────────────────────────────────── */}
      <div className='bg-white border-b border-gray-200 px-6 py-4'>
        <h1 className='text-2xl font-black tracking-tight text-gray-900'>
          Advanced Dashboard
        </h1>
        <p className='text-sm text-gray-500 mt-0.5'>
          Aggregated sensor analytics across your IoT fleet
        </p>
      </div>

      {/* ── Sticky global filter bar ──────────────────────────────────────────── */}
      <div className='sticky top-0 z-30 bg-white border-b border-gray-200 shadow-sm'>
        <div className='px-6 py-3 flex flex-wrap items-center gap-3'>
          <div className='flex items-center gap-2'>
            <span className='text-xs font-semibold text-gray-500 whitespace-nowrap'>
              Controller
            </span>
            <ControllerMultiSelect
              controllers={controllers}
              selected={selectedControllers}
              onChange={ids => {
                setSelectedControllers(ids)
                setFetched(false)
              }}
            />
          </div>

          <div className='w-px h-6 bg-gray-200 hidden sm:block' />

          <div className='flex items-center gap-2'>
            <span className='text-xs font-semibold text-gray-500'>From</span>
            <div className='border border-gray-200 rounded-lg overflow-hidden'>
              <DateTimePicker12h
                value={fromDT}
                onChange={v => {
                  setFromDT(v)
                  setFetched(false)
                }}
              />
            </div>
          </div>
          <div className='flex items-center gap-2'>
            <span className='text-xs font-semibold text-gray-500'>To</span>
            <div className='border border-gray-200 rounded-lg overflow-hidden'>
              <DateTimePicker12h
                value={toDT}
                onChange={v => {
                  setToDT(v)
                  setFetched(false)
                }}
              />
            </div>
          </div>

          <div className='w-px h-6 bg-gray-200 hidden sm:block' />

          <button
            onClick={handleGenerate}
            disabled={isLoading || noControllerSelected}
            className='flex items-center gap-2 px-4 py-2 bg-blue-900 text-white rounded-lg text-sm font-semibold hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
          >
            {isLoading ? (
              <svg
                className='w-4 h-4 animate-spin'
                fill='none'
                viewBox='0 0 24 24'
              >
                <circle
                  className='opacity-25'
                  cx='12'
                  cy='12'
                  r='10'
                  stroke='currentColor'
                  strokeWidth='4'
                />
                <path
                  className='opacity-75'
                  fill='currentColor'
                  d='M4 12a8 8 0 018-8v8H4z'
                />
              </svg>
            ) : (
              <svg
                className='w-4 h-4'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
                strokeWidth={2}
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M5 3l14 9-14 9V3z'
                />
              </svg>
            )}
            {isLoading ? 'Loading…' : 'Generate'}
          </button>

          <div className='ml-auto flex items-center gap-2'>
            {isEditMode && (
              <button
                onClick={handleResetLayout}
                className='flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-white border border-gray-200 text-gray-500 hover:bg-red-50 hover:border-red-200 hover:text-red-600 transition-colors'
              >
                <svg
                  className='w-3.5 h-3.5'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
                  />
                </svg>
                Reset Layout
              </button>
            )}
            <button
              onClick={() => {
                setIsEditMode(e => !e)
                setShowWidgetPicker(false)
              }}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                isEditMode
                  ? 'bg-amber-50 border-amber-300 text-amber-700'
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <svg
                className='w-4 h-4'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
                strokeWidth={2}
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'
                />
              </svg>
              {isEditMode ? 'Done' : 'Edit Layout'}
            </button>
            {isEditMode && (
              <button
                onClick={() => setShowWidgetPicker(true)}
                className='flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-white border border-gray-200 text-gray-600 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-colors'
              >
                <svg
                  className='w-4 h-4'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                  strokeWidth={2.5}
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    d='M12 4v16m8-8H4'
                  />
                </svg>
                Add Widget
              </button>
            )}
          </div>
        </div>

        {/* Comparison notice */}
        {selectedControllers.length > 1 && (
          <div className='px-6 pb-2'>
            <div className='inline-flex items-center gap-2 text-xs text-blue-700 bg-blue-50 border border-blue-100 rounded-lg px-3 py-1.5'>
              <svg
                className='w-3.5 h-3.5 flex-shrink-0'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
                strokeWidth={2}
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                />
              </svg>
              Comparison mode — {selectedControllers.length} controllers · Add a
              Comparison widget to compare them side-by-side
            </div>
          </div>
        )}
      </div>

      {/* ── Widget grid ───────────────────────────────────────────────────────── */}
      <div className='flex-1 p-6'>
        {/* Layout loading */}
        {layoutLoading ? (
          <div className='flex items-center justify-center py-24'>
            <div className='flex flex-col items-center gap-3'>
              <svg
                className='w-8 h-8 animate-spin text-blue-600'
                fill='none'
                viewBox='0 0 24 24'
              >
                <circle
                  className='opacity-25'
                  cx='12'
                  cy='12'
                  r='10'
                  stroke='currentColor'
                  strokeWidth='4'
                />
                <path
                  className='opacity-75'
                  fill='currentColor'
                  d='M4 12a8 8 0 018-8v8H4z'
                />
              </svg>
              <span className='text-sm font-medium text-gray-500'>
                Loading layout…
              </span>
            </div>
          </div>
        ) : noControllerSelected ? (
          /* No controller selected */
          <div className='flex flex-col items-center justify-center py-24 text-gray-400'>
            <svg
              className='w-14 h-14 mb-4 opacity-20'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={1.5}
                d='M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18'
              />
            </svg>
            <p className='text-base font-semibold text-gray-500'>
              Select a controller to get started
            </p>
            <p className='text-sm text-gray-400 mt-1'>
              Use the Controller dropdown above, then click Generate
            </p>
          </div>
        ) : activeWidgets.length === 0 && !isEditMode ? (
          /* No widgets configured yet */
          <div className='flex flex-col items-center justify-center py-24 text-gray-400'>
            <svg
              className='w-14 h-14 mb-4 opacity-20'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={1.5}
                d='M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z'
              />
            </svg>
            <p className='text-base font-semibold text-gray-500'>
              No widgets configured
            </p>
            <p className='text-sm text-gray-400 mt-1'>
              Click{' '}
              <span className='font-semibold text-gray-500'>Edit Layout</span>{' '}
              to add widgets to your dashboard
            </p>
          </div>
        ) : (
          /* Widget grid */
          <div className='relative grid grid-cols-2 gap-5'>
            {/* Loading skeleton overlay */}
            {isLoading && (
              <div className='absolute inset-0 bg-gray-50/80 backdrop-blur-[2px] z-20 flex items-center justify-center rounded-xl'>
                <div className='bg-white rounded-2xl shadow-lg px-8 py-6 flex flex-col items-center gap-3'>
                  <svg
                    className='w-8 h-8 animate-spin text-blue-600'
                    fill='none'
                    viewBox='0 0 24 24'
                  >
                    <circle
                      className='opacity-25'
                      cx='12'
                      cy='12'
                      r='10'
                      stroke='currentColor'
                      strokeWidth='4'
                    />
                    <path
                      className='opacity-75'
                      fill='currentColor'
                      d='M4 12a8 8 0 018-8v8H4z'
                    />
                  </svg>
                  <span className='text-sm font-medium text-gray-600'>
                    Fetching sensor data…
                  </span>
                </div>
              </div>
            )}

            {activeWidgets.map(w => (
              <WidgetCard
                key={w.id}
                widget={w}
                isEditMode={isEditMode}
                isDragOver={dragOverId === w.id}
                onRemove={() => handleRemoveWidget(w.id)}
                onSpanChange={span => handleSpanChange(w.id, span)}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onDragEnd={handleDragEnd}
              >
                {renderContent(w)}
              </WidgetCard>
            ))}

            {isEditMode && activeWidgets.length === 0 && (
              <div className='col-span-2 flex flex-col items-center justify-center py-20 border-2 border-dashed border-gray-200 rounded-xl text-gray-400'>
                <svg
                  className='w-10 h-10 mb-3 opacity-25'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={1.5}
                    d='M12 4v16m8-8H4'
                  />
                </svg>
                <p className='text-sm'>
                  Click <span className='font-semibold'>Add Widget</span> to
                  build your layout
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Widget picker modal ───────────────────────────────────────────────── */}
      {showWidgetPicker && (
        <WidgetPickerModal
          onAdd={handleAddWidget}
          onClose={() => setShowWidgetPicker(false)}
        />
      )}
    </div>
  )
}

export default AnalyticsDashboard
